import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import moment from "moment"
import styled from "@emotion/styled"
import { darken } from "polished"
import Color from "color"

import Bot from "./ChatBubble/Bot"
import User from "./ChatBubble/User"
import { timestampNormalizer } from "./util"

import "./ChatVisualizer.scss"

const LUM_THRESHOLD = 0.6

const propTypes = {
  chats: PropTypes.array,
  readOnly: PropTypes.bool,
  botOnly: PropTypes.bool,
  noProfilePic: PropTypes.bool,
  botProfilePic: PropTypes.string,
  userProfilePic: PropTypes.string,
  themeColor: PropTypes.string,
}

const defaultProps = {
  chats: [],
  readOnly: false,
  botOnly: false,
  noProfilePic: false,
  botProfilePic: "",
  userProfilePic: "",
  themeColor: "#173753",
}

const SystemDisplay = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px;

  >.date {
    padding: 4px 16px;
    border-radius: 4px;
    background-color: ${(props) => {
      if (props.color) {
        const bg = Color(props.color)
        if (bg.luminosity() > LUM_THRESHOLD) {
          return darken(0.18, "#0084FF")
        }
        return darken(0.09, props.color)
      }
      return darken(0.18, "#0084FF")
    }};
    color: #fff;
    font-size: 0.8rem;
    margin: 16px 0;
  }

  >.text {
    padding: 4px 16px;
    border-radius: 4px;
    background-color: ${(props) => {
      if (props.color) {
        const bg = Color(props.color)
        if (bg.luminosity() > LUM_THRESHOLD) {
          return darken(0.18, "#0084FF")
        }
        return darken(0.18, props.color)
      }
      return darken(0.18, "#0084FF")
    }};
    color: #fff;
    font-size: 0.8rem;
    margin: 16px 0;
  }
`

class ChatVisualizer extends React.Component {
  
  componentDidMount() {
    this.scrollToBottom({
      animated: false
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.chats.length !== prevProps.chats.length && this.props.chats.length) {
      if (!this.props.loadingHistory) {
        this.scrollToBottom({
          animated: !!prevProps.chats.length
        })
      }
    }
  }

  scrollToBottom({
    animated = false
  }) {
    if (this.chatEnd) {
      setTimeout(() => {
        this.chatEnd?.scrollIntoView({
          block: "end",
          behavior: animated ? "smooth" : "auto"
        })
      }, 500)
    }
  }

  render() {
    const {
      chats,
      botProfilePic,
      userProfilePic,
      readOnly,
      botOnly,
      noProfilePic,
      themeColor,
      onResend
    } = this.props

    const newChats = []
    if (chats.length) {
      chats.forEach((chat, i) => {
        if (!_.get(chat.messageEvent, "hidden")) {
          if (chat.messageEvent) {
            if ((
                i > 0 &&
                (
                  (
                    timestampNormalizer(chat?.messageEvent?.timestamp) &&
                    timestampNormalizer(chats[i - 1]?.messageEvent?.timestamp) &&
                    moment(timestampNormalizer(chat?.messageEvent?.timestamp)).date() !== moment(timestampNormalizer(chats[i - 1]?.messageEvent?.timestamp)).date()
                  ) ||
                  (
                    timestampNormalizer(chat?.createdAt) &&
                    timestampNormalizer(chats[i - 1]?.createdAt) &&
                    moment(timestampNormalizer(chat?.createdAt)).date() !== moment(timestampNormalizer(chats[i - 1]?.createdAt)).date()
                  )
                )
              ) 
            ||
              (
                i === 0 &&
                timestampNormalizer(chat?.createdAt) &&
                chats?.length === 1
              )
            ) {
              newChats.push(
                <SystemDisplay
                  color={themeColor}
                  key={chat.createdAt || chat.messageEvent.timestamp}
                >
                  <div className="date">{timestampNormalizer(chat.createdAt || chat.messageEvent.timestamp) ? moment(timestampNormalizer(chat.createdAt || chat.messageEvent.timestamp)).format("ll") : null}</div>
                </SystemDisplay>
              )
            }
            if (chat.from === "BOT") {
              if (chat.messageEvent.type !== "WEB_ACTION") {      
                newChats.push(
                  <Bot
                    key={chat._id}
                    message={chat.messageEvent}
                    profilePic={botProfilePic}
                    noProfilePic={noProfilePic}
                    readOnly={readOnly}
                    themeColor={themeColor}
                    isLast={chats.length === i + 1}
                    onAction={(action) => {
                      if (!readOnly) {
                        this.props.onAction(action)
                      }
                    }}
                  />
                )
              }
            } else if (chat.from === "MEMBER" && chat.messageEvent.type === "SYSTEM") {
              newChats.push(
                <SystemDisplay
                  color={themeColor}
                  key={chat._id}
                >
                  <div className="text">{chat.messageEvent.data.body}</div>
                </SystemDisplay>
              )
            } else if (chat.from === "MEMBER" && chat.memberId !== this.props.memberId) {
              if (chat.messageEvent.type !== "WEB_EVENT") {
                newChats.push(
                  <User
                    key={chat?.clientMutationId || chat._id || i}
                    message={chat.messageEvent}
                    profilePic={userProfilePic}
                    thirdParty={true}
                    themeColor={themeColor}
                    firstName={chat.member.firstName}
                    error={chat?.error}
                    createdAt={chat?.createdAt}
                    onResend={onResend}
                    clientMutationId={chat?.clientMutationId}
                  />
                )
              }
            } else {
              if (chat.messageEvent.type !== "WEB_EVENT") {
                newChats.push(
                  <User
                    key={chat?.clientMutationId || chat._id || i}
                    message={chat.messageEvent}
                    profilePic={userProfilePic}
                    themeColor={themeColor}
                    error={chat?.error}
                    createdAt={chat?.createdAt}
                    onResend={onResend}
                    clientMutationId={chat?.clientMutationId}
                  />
                )
              }
            }
          }
        }
      })

      if (_.get(chats, "[0].messageEvent.timestamp") && chats?.length > 1) {
        newChats.unshift(
          <SystemDisplay
            color={themeColor}
            key={chats[0].messageEvent.timestamp}
          >
            <div className="date">{chats[0].messageEvent && timestampNormalizer(chats[0].messageEvent.timestamp) ? moment(timestampNormalizer(chats[0].messageEvent.timestamp)).format("ll") : null}</div>
          </SystemDisplay>
        )
      }
    }
    return (
      <div
        ref={this.props.chatVisualizerRef}
        className={`chat-visualizer-container ${botOnly ? "bot-only" : ""}`}
      >
        {newChats}
        <div
          ref={(c) => { this.chatEnd = c }}
          className="scroll-end-dummy"
        >
        </div>
      </div>
    )
  }
}

ChatVisualizer.propTypes = propTypes
ChatVisualizer.defaultProps = defaultProps

export default ChatVisualizer
